import React from "react";
import moment from "moment";
import * as Glob from '../../Globale';
import { CampoData, CampoNumero, Tipo, Trad, ValidationResult } from "../../components";
import useReducerOnChange from "../../hooks/ReducerOnChange";
import { Companies } from "./configDPS";
import { ContractTypes, Distraints, DurationValues, GenderTypes, MinNetSalary, MonthsNumber, WorkingHoursTypes } from "./Constants";
import { CDataPrimaryAnalyser, CDataLiabilityItem } from "../../form/DigitalProductAnalyser/PrimaryAnalyser";
import { getInsuranceAge } from "./helpers";


interface ILoanFormDPSProps {
    FormData: CDataPrimaryAnalyser;
    LiabilitiesData: CDataLiabilityItem[];
    OnChange: (e: React.FormEvent<any> | string, valore?: any) => void;
    TotalEmploymentLength: any;
}

class CDataLoanFormDPS {
    public ContractType: string = "Undetermined";
    public DebtorIsMilitary: boolean = false;
    public Gender: string = "Man";
    public Installment: number = 0;
    public IsTrialPeriodFinished: boolean = false;
    public MedicalQuestionnaire: boolean = false;
    public NetSalary: number = 0;
    public SickDays: number = 0;
    public WorkingHoursType: string = "FullTime";
    public WorkTimePercentage: number = 100;
}

export const LoanFormDPS: React.FC<ILoanFormDPSProps> = ({ FormData, LiabilitiesData, OnChange, TotalEmploymentLength }) => {
    const { DateOfBirth, Duration, EffectiveDate, EmploymentDate } = { ...FormData };
    const [Data, setData] = useReducerOnChange<CDataLoanFormDPS>(new CDataLoanFormDPS());

    const getCompaniesInstallmentReduction = (): boolean[] => {
        return Companies.map((company: any) => {
            const { PensionAge, CombinedPensionAge, PensionMonthsOfWork, CombinedPensionMonthsOfWork } = Data.DebtorIsMilitary ? company.DebtorIsMilitary.Military : company.DebtorIsMilitary;
            const totalEmploymentLengthMonths = TotalEmploymentLength.years() * MonthsNumber.Year + TotalEmploymentLength.months();
            const LoanAgeTreshold = initialAge + durationYears;
            const LoanEmploymentTreshold = totalEmploymentLengthMonths + parseInt(Duration);

            return (
                (LoanAgeTreshold >= CombinedPensionAge && LoanEmploymentTreshold >= CombinedPensionMonthsOfWork) ||
                LoanAgeTreshold >= PensionAge ||
                LoanEmploymentTreshold >= PensionMonthsOfWork[Data.Gender]
            );
        });
    };

    const getCompaniesResidualDebt = (): number[] => {
        return Companies.map((company: any) => {
            return LiabilitiesData.filter(item => item.Insurer == company.CompanyName).reduce((total, item) => total + item.ResidualDebt, 0);
        });
    };

    const getDisponibilitaProdotto100x100Unicredit = (initialAge: number): boolean => {
        const seniorityNotExceeded = TotalEmploymentLength.years() < 33;
        const endAge = initialAge + parseInt(Duration) / MonthsNumber.Year;

        if (initialAge <= 56) {
            return endAge >= 62;
        } else if (initialAge >= 61) {
            return endAge >= 68 ? false : !seniorityNotExceeded;
        }

        return Companies[0]["DisponibilitaProdotto100x100Unicredit"][`_${initialAge}_${seniorityNotExceeded}_${Duration}`];
    };

    const insuranceAmount = Data.Installment * parseInt(Duration);
    const durationYears = parseInt(Duration) / MonthsNumber.Year;
    const initialAge = moment(EffectiveDate).diff(DateOfBirth, "years");
    const initialEmploymentLength = moment(EffectiveDate).diff(EmploymentDate, "years");
    const companiesInstallmentReduction: boolean[] = getCompaniesInstallmentReduction();
    const insuranceAge = getInsuranceAge(EffectiveDate, DateOfBirth);
    const companiesResidualDebt: number[] = getCompaniesResidualDebt();

    const installmentsSum = LiabilitiesData.reduce((total, item) => total + item.Amount, 0);
    const cessionSum = LiabilitiesData.filter(item => item.LiabilityType == "Cessione").reduce((total, item) => total + item.Amount, 0);

    return (
        <div data-sticky-container>
            <div
                data-sticky="true"
                data-margin-top="135"
                data-sticky-class="kt-sticky"
                data-sticky-wrap="true"
                data-sticky-for="1024"
                className="form-group kt-form login-form__group row dashedborder"
            >
                <div className="col-lg-4 offset-lg-4 align-self-center TextCenter kt-padding-t-10 kt-padding-b-15">
                    <img alt="Logo" src="images/companies/logo_gaiil.svg" width="180" />
                </div>
                <div className="col-lg-4 align-self-center TextCenter">
                    <img alt="Logo" src="images/companies/logo_sgi.png" width="250" />
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Tipologia Contratto di Lavoro</label>
                    <Tipo
                        Dati={ContractTypes}
                        Campo="ContractType"
                        ID_ChiaveString={Data.ContractType}
                        TradValori={false}
                        OnAggiorna={(e, v) =>
                            setData({
                                Type: "OnChangeFromValue",
                                Name: e,
                                Value: v
                            })
                        }
                        Richiesto
                    />
                </div>
                {Companies.map((company: any) => (
                    <ValidationResult Message={company.ContractType[Data.ContractType]} />
                ))}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Tipologia Orario di Lavoro</label>
                    <Tipo
                        Dati={WorkingHoursTypes}
                        Campo="WorkingHoursType"
                        TradValori={false}
                        ID_ChiaveString={Data.WorkingHoursType}
                        OnAggiorna={(e, v) =>
                            setData({
                                Type: "OnChangeFromValue",
                                Name: e,
                                Value: v
                            })
                        }
                        Richiesto
                    />
                </div>
                {Companies.map((company: any) => (
                    <ValidationResult Message={company.WorkingHoursType[Data.WorkingHoursType]} />
                ))}
            </div>
            {Data.WorkingHoursType != "FullTime" && (
                <div className="form-group kt-form login-form__group row dashedborder">
                    <div className="col-lg-4">
                        <label>Percentuale Part Time</label>
                        <div className="kt-input-icon kt-input-icon--right">
                            <CampoNumero
                                Id="WorkTimePercentage"
                                Name="WorkTimePercentage"
                                Valore={Data.WorkTimePercentage}
                                OnChange={(e, v) =>
                                    setData({
                                        Type: "OnChangeFromHybrid",
                                        NameEvent: Object.assign({}, e),
                                        Value: v
                                    })
                                }
                                CSS="form-control kt-input"
                                Precisione={0}
                                Icona="fa fa-percentage"
                                Minimo={0}
                                Massimo={100}
                                BarraLimiti
                            />
                        </div>
                    </div>
                    {Companies.map((company: any) => {
                        const message = Data.WorkTimePercentage < company.WorkTimePercentage.MinValue ? company.WorkTimePercentage.Message : "";
                        return <ValidationResult Message={message} />;
                    })}
                </div>
            )}
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Sesso</label>
                    <Tipo
                        Dati={GenderTypes}
                        Campo="Gender"
                        ID_ChiaveString={Data.Gender}
                        TradValori={false}
                        OnAggiorna={(e, v) =>
                            setData({
                                Type: "OnChangeFromValue",
                                Name: e,
                                Value: v
                            })
                        }
                        Richiesto
                    />
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Salario Netto Mensile</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <CampoNumero
                            Id="NetSalary"
                            Name="NetSalary"
                            Valore={Data.NetSalary}
                            Minimo={MinNetSalary}
                            OnChange={(e, v) =>
                                setData({
                                    Type: "OnChangeFromHybrid",
                                    NameEvent: Object.assign({}, e),
                                    Value: v
                                })
                            }
                            Precisione={2}
                            CSS="form-control kt-input"
                            Icona={"fa " + Glob.ValutaSimbolo}
                        />
                    </div>
                </div>
                {Companies.map((company: any) => {
                    const isCommitmentThresholdExceeded: boolean = Data.Installment + installmentsSum > Data.NetSalary * company.CoexistencesMax;
                    const isPovertyThresholdReached: boolean = Data.NetSalary - installmentsSum - Data.Installment < company.PovertyMin;
                    const message =
                        (Data.NetSalary === 0 && "Lo Stipendio Netto Mensile NON può essere Nullo") ||
                        (isCommitmentThresholdExceeded &&
                            isPovertyThresholdReached &&
                            `La somma delle Coesistenze attuali pari ad ${Trad.TradParam("{1-I}", [installmentsSum])} e della rata proposta del finanziamento pari ad ${Trad.TradParam("{1-I}", [
                                Data.Installment
                            ])} rendono lo Stipendio Disponibile al di sotto della soglia di povertà fissata ad ${Trad.TradParam("{1-I}", [company.PovertyMin])}`) ||
                        (!isCommitmentThresholdExceeded &&
                            isPovertyThresholdReached &&
                            `La rata proposta del finanziamento pari ad ${Trad.TradParam("{1-I}", [
                                Data.Installment
                            ])} rende lo Stipendio Disponibile al di sotto della soglia di povertà fissata ad ${Trad.TradParam("{1-I}", [company.PovertyMin])}`) ||
                        (isCommitmentThresholdExceeded &&
                            !isPovertyThresholdReached &&
                            `In Relazione al Totale delle Coesistenze attuali del Cedente pari ad ${Trad.TradParam("{1-I}", [
                                installmentsSum
                            ])}, la rata proposta del finanziamento pari ad ${Trad.TradParam("{1-I}", [Data.Installment])} supera la soglia del ${Trad.TradParam("{1-P}", [
                                company.CoexistencesMax * 100
                            ])} dello Stipendio Mensile Netto pari ad  ${Trad.TradParam("{1-I}", [Data.NetSalary])}`) ||
                        "";

                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Rata Ceduta</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <CampoNumero
                            Id="Installment"
                            Name="Installment"
                            Valore={Data.Installment}
                            OnChange={(e, v) =>
                                setData({
                                    Type: "OnChangeFromHybrid",
                                    NameEvent: Object.assign({}, e),
                                    Value: v
                                })
                            }
                            Precisione={2}
                            CSS="form-control kt-input"
                            Icona={"fa " + Glob.ValutaSimbolo}
                        />
                    </div>
                </div>
                {Companies.map((company: any, index: number) => {
                    const distraintSum = LiabilitiesData.filter(item => Distraints.includes(item.LiabilityType)).reduce((total, item) => total + item.Amount, 0);
                    const maxInstallmentExceeded = Data.Installment > Data.NetSalary / 5;
                    const messageId = `${maxInstallmentExceeded ? "Max" : "NoMax"}${!!distraintSum ? "Distraint" : "NoDistraint"}${
                        companiesInstallmentReduction[index] ? "InstallmentReduction" : "NoInstallmentReduction"
                    }`;
                    const messageType = Data.DebtorIsMilitary ? company.Installment.Messages.Military[messageId] : company.Installment.Messages[messageId];
                    const message = (Data.Installment === 0 && company.Installment.Messages.EmptyInstallment) || Trad.TradParam(messageType, [Data.NetSalary / 5]) || "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Durata Finanziamento</label>
                    <Tipo Dati={DurationValues} Campo="Duration" ID_ChiaveString={Duration} TradValori={false} OnAggiorna={OnChange} Richiesto />
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Data Decorrenza</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <div className="form-control">
                            <CampoData
                                ValoreInArrivo={false}
                                Name="EffectiveDate"
                                Id="EffectiveDate"
                                Valore={EffectiveDate}
                                OnChange={OnChange}
                                DataMinima={moment().add(-90, "day").min(EmploymentDate).toDate()}
                                DataMassima={moment().add(90, "day").min(EmploymentDate).toDate()}
                            />
                        </div>
                    </div>
                </div>
                {Companies.map((company: any) => {
                    const message = initialEmploymentLength < company.CurrentEmployment.MaxLength ? "" : company.CurrentEmployment.Message;
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Capitale da Assicurare</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <CampoNumero
                            Id="InsuranceAmount"
                            Name="InsuranceAmount"
                            Valore={insuranceAmount}
                            OnChange={(e, v) => {}}
                            Precisione={2}
                            CSS="form-control kt-input"
                            Icona={"fa " + Glob.ValutaSimbolo}
                            Disabilitato
                        />
                    </div>
                </div>
                {Companies.map((company: any) => {
                    let message = insuranceAmount > company.InsuranceAmount.MaxValue ? company.InsuranceAmount.Message : "";
                    if (company.InsuranceAmount.BlockedValue && insuranceAmount > company.InsuranceAmount.BlockedValue) {
                        message = company.InsuranceAmount.BlockedValueMessage;
                    }

                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-12">
                    <label>Tot.Coesistenze</label>
                </div>
                {Companies.map((company: any, index: number) => {
                    return (
                        <div className="col-lg-2">
                            <label>{company.CompanyName}</label>
                            <div className="kt-input-icon kt-input-icon--right">
                                <CampoNumero
                                    Id={`${company.CompanyName}Loans`}
                                    Name={`${company.CompanyName}Loans`}
                                    Valore={companiesResidualDebt[index]}
                                    OnChange={(e, v) => {}}
                                    Precisione={2}
                                    CSS="form-control kt-input"
                                    Icona={"fa " + Glob.ValutaSimbolo}
                                    Disabilitato
                                />
                            </div>
                        </div>
                    );
                })}

                {Companies.map((company: any, index: number) => {
                    const companyTotalDebt = companiesResidualDebt[index] ? companiesResidualDebt[index] + insuranceAmount : 0;
                    const message =
                        (!cessionSum && company.TotalResidualDebt.NoCessionMessage) ||
                        (companyTotalDebt > company.TotalResidualDebt.MaxValue && company.TotalResidualDebt.Message) ||
                        (companyTotalDebt > company.TotalResidualDebt.MiddleValue && company.TotalResidualDebt.MiddleValueMessage) ||
                        "";

                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Estinzione Impegni</label>
                </div>
                {Companies.map((company: any) => {
                    const hasNotCessionLiabilities = LiabilitiesData.filter(item => item.LiabilityType != "Cessione").length;

                    return <ValidationResult Message={hasNotCessionLiabilities ? company.OtherLoansEnd.Message : ""} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Il Cedente è un Militare o Paramilitare</label>
                    <div className="kt-radio-inline inlinea">
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value="True"
                                type="radio"
                                checked={Data.DebtorIsMilitary}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "DebtorIsMilitary",
                                        Value: true
                                    });
                                }}
                            />
                            SI<span></span>
                        </label>
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value={"false"}
                                type="radio"
                                checked={!Data.DebtorIsMilitary}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "DebtorIsMilitary",
                                        Value: false
                                    });
                                }}
                            />
                            NO<span></span>
                        </label>
                    </div>
                </div>
                {Companies.map((company: any, index: number) => {
                    const endAge = initialAge + durationYears;
                    const endMonthsOfWork = TotalEmploymentLength.years() * MonthsNumber.Year + TotalEmploymentLength.months() + parseInt(Duration);
                    const Raggiunto_Parametro_solo_Combinato =
                        ((endAge >= company.DebtorIsMilitary.CombinedPensionAge && endMonthsOfWork >= company.DebtorIsMilitary.CombinedPensionMonthsOfWork) ||
                            (endAge >= company.DebtorIsMilitary.Military.CombinedPensionAge && endMonthsOfWork >= company.DebtorIsMilitary.Military.CombinedPensionMonthsOfWork)) &&
                        !(
                            ((endAge >= company.DebtorIsMilitary.Military.PensionAge || endMonthsOfWork >= company.DebtorIsMilitary.Military.PensionMonthsOfWork[Data.Gender]) &&
                                endAge >= company.DebtorIsMilitary.PensionAge) ||
                            endMonthsOfWork >= company.DebtorIsMilitary.PensionMonthsOfWork[Data.Gender]
                        );
                    const GAIIL_Entro_Parametri_Anzianità_Massima = index == 0 && TotalEmploymentLength.years() + durationYears <= company.TotalEmploymentLength[Data.Gender];
                    const Disponibilità_Prodotto_Quota_100 =
                        index == 0 && Raggiunto_Parametro_solo_Combinato && GAIIL_Entro_Parametri_Anzianità_Massima ? getDisponibilitaProdotto100x100Unicredit(initialAge) : false;
                    const message =
                        (companiesInstallmentReduction[index] &&
                            ((Data.DebtorIsMilitary &&
                                ((Raggiunto_Parametro_solo_Combinato && company.DebtorIsMilitary.Military.Messages.Combined) || company.DebtorIsMilitary.Military.Messages.NotCombined)) ||
                                company.DebtorIsMilitary.Messages[
                                    `${Data.Gender}${Raggiunto_Parametro_solo_Combinato ? "Combined" : "NotCombined"}${Disponibilità_Prodotto_Quota_100 ? "Available" : "NotAvailable"}`
                                ])) ||
                        "";

                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Data Assunzione</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <div className="form-control">
                            <CampoData
                                ValoreInArrivo={false}
                                Name="EmploymentDate"
                                Id="EmploymentDate"
                                Valore={EmploymentDate}
                                DataMinima={new Date("1901-01-01")}
                                DataMassima={new Date(Date.now())}
                                OnChange={OnChange}
                            />
                        </div>
                    </div>
                </div>
                {Companies.map((company: any) => {
                    const message =
                        (EmploymentDate < DateOfBirth && company.EmploymentDate.Messages.LessThanBirthDay) ||
                        (EmploymentDate > moment().add(-company.EmploymentDate.MinEmploymentMonths, "months").toDate() && company.EmploymentDate.Messages.MinEmploymentMonths) ||
                        "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Superamento Periodo di Prova</label>
                    <div className="kt-radio-inline inlinea">
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value="true"
                                type="radio"
                                checked={Data.IsTrialPeriodFinished}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "IsTrialPeriodFinished",
                                        Value: true
                                    });
                                }}
                            />
                            SI<span></span>
                        </label>
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value="false"
                                type="radio"
                                checked={!Data.IsTrialPeriodFinished}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "IsTrialPeriodFinished",
                                        Value: false
                                    });
                                }}
                            />
                            NO<span></span>
                        </label>
                    </div>
                </div>
                {Companies.map((company: any) => {
                    const message = !Data.IsTrialPeriodFinished && company.IsTrialPeriodFinished.TrialPeriod ? company.IsTrialPeriodFinished.Message : "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Anzianità di Servizio</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <input type="text" className="form-control kt-input" value={`Anni ${TotalEmploymentLength.years()} e Mesi ${TotalEmploymentLength.months()}`} readOnly />
                    </div>
                </div>
                <div className="col-lg-8 align-self-center TextCenter">
                    <div className="alert alert-solid-warning alert-bold">
                        <div className="alert-text">
                            Qualora fosse presente Anzianità di servizio pregressa, è necessario effettuare il calcolo dell'anzianità di servizio del Cedente utilizzando il menù "Anzianità di
                            Servizio"
                        </div>
                    </div>
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Data di Nascita</label>
                    <div className="kt-input-icon kt-input-icon--right">
                        <div className="form-control">
                            <CampoData
                                ValoreInArrivo={false}
                                Name="DateOfBirth"
                                Id="DateOfBirth"
                                Valore={DateOfBirth}
                                DataMinima={moment(EffectiveDate).add(-75, "year").toDate()}
                                DataMassima={moment(EffectiveDate).add(-18, "year").toDate()}
                                OnChange={OnChange}
                            />
                        </div>
                    </div>
                </div>
                {Companies.map((company: any) => {
                    const message = initialAge > company.DateOfBirth.MaxAge ? company.DateOfBirth.Message : "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-12">
                    <label>Età Inizio Piano</label>
                </div>
                <div className="col-lg-2">
                    <label>Assicurativa</label>
                    <input type="text" className="form-control kt-input" value={insuranceAge} readOnly />
                </div>
                <div className="col-lg-2">
                    <label>Anagrafica</label>
                    <input type="text" className="form-control kt-input" value={initialAge} readOnly />
                </div>
                {Companies.map((company: any) => {
                    const message = insuranceAge > company.InsuranceAge.MaxValue ? company.InsuranceAge.Message : "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-12">
                    <label>Età Scadenza Piano</label>
                </div>
                <div className="col-lg-2">
                    <label>Assicurativa</label>
                    <input type="text" className="form-control kt-input" value={insuranceAge + durationYears} readOnly />
                </div>
                <div className="col-lg-2">
                    <label>Anagrafica</label>
                    <input type="text" className="form-control kt-input" value={initialAge + durationYears} readOnly />
                </div>
                {Companies.map((company: any) => {
                    const message = insuranceAge + durationYears > company.FinalInsuranceAge.MaxValue ? company.FinalInsuranceAge.Message : "";
                    return <ValidationResult Message={message} />;
                })}
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Il Cedente è in grado di sottoscrivere DBS/Questionario Medico?</label>
                    <div className="kt-radio-inline inlinea">
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value="True"
                                type="radio"
                                checked={Data.MedicalQuestionnaire}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "MedicalQuestionnaire",
                                        Value: true
                                    });
                                }}
                            />
                            SI<span></span>
                        </label>
                        <label className="kt-radio kt-radio--solid kt-radio--state-success">
                            <input
                                value={"false"}
                                type="radio"
                                checked={!Data.MedicalQuestionnaire}
                                onChange={e => {
                                    setData({
                                        Type: "OnChangeFromValue",
                                        Name: "MedicalQuestionnaire",
                                        Value: false
                                    });
                                }}
                            />
                            NO<span></span>
                        </label>
                    </div>
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Giorni di Malattia</label>
                    <div className="kt-input">
                        <CampoNumero
                            Id="SickDays"
                            Name="SickDays"
                            Valore={Data.SickDays}
                            OnChange={(e, v) =>
                                setData({
                                    Type: "OnChangeFromHybrid",
                                    NameEvent: Object.assign({}, e),
                                    Value: v
                                })
                            }
                            CSS="form-control kt-input"
                            Precisione={0}
                            Minimo={0}
                            Massimo={999}
                        />
                    </div>
                </div>
            </div>
            <div className="form-group kt-form login-form__group row dashedborder">
                <div className="col-lg-4">
                    <label>Documentazione Richiesta</label>
                </div>
                {Companies.map((company: any, index: number) => {
                    const contributingExtracts = (initialAge >= company.MaxAgeMilitaryContributionExtracts && Data.DebtorIsMilitary) || initialAge >= company.MaxAgeContributionExtracts;
                    const isMaxSickDaysExceeded = Data.SickDays > company.MaxSickDays;
                    const rvm = insuranceAmount + companiesResidualDebt[index] > company.RVMLimit || !Data.MedicalQuestionnaire || Data.SickDays > company.MaxRVMSickDays;
                    const message = `${contributingExtracts ? "ContributingExtracts" : "NoContributingExtracts"}${isMaxSickDaysExceeded ? "MaxSickDaysExceeded" : "MaxSickDaysNotExceeded"}${
                        rvm ? "Rvm" : "NoRvm"
                    }`;
                    return <ValidationResult Message={company.Documentation.Messages[message]} />;
                })}
            </div>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/DigitalProductAnalyser/LoanFormDPS.tsx